/**
 * API Client — Axios wrapper with JWT auth and token refresh
 *
 * Handles both org user and ops user authentication:
 *   - Attaches Bearer token from localStorage
 *   - For ops users with active org access, attaches X-Ops-Access-Token header
 *   - On 401, attempts refresh (org via /auth/refresh, ops via /ops/auth/refresh)
 *   - Falls back to respective login page if refresh fails
 *
 * Race-safe refresh: when multiple requests get 401 simultaneously, only ONE
 * refresh call is made. All queued requests wait on the same promise and replay
 * with the new token once the single refresh completes.
 */

import axios from 'axios';
import { safeNext } from './safeNext';
import { extractApiErrorMessage } from './apiError';

const API_BASE = '/api/v1';

/**
 * Build a login redirect that preserves the current destination as `?next=`,
 * so a hard-401 sign-out returns the user where they were headed after they
 * re-authenticate (spec 102 §4.6, Wave 0). Uses the shared `safeNext` guard,
 * so auth/enter routes and unsafe targets are dropped (→ bare login).
 */
function loginUrlWithNext(base: string): string {
  if (typeof window === 'undefined') return base;
  // Only the PM login page (`/login`) reads `?next=` today; don't dangle an
  // ignored param on the ops login (review LOW-6).
  if (base !== '/login') return base;
  const dest = safeNext(window.location.pathname + window.location.search);
  return dest && dest !== '/' ? `${base}?next=${encodeURIComponent(dest)}` : base;
}

/**
 * True for the pre-authentication endpoints — login, login-code request/verify,
 * and password reset (staff, ops, resident, vendor). A 401 from these means BAD
 * CREDENTIALS (wrong code / wrong password), NOT an expired session, so the
 * response-interceptor must NOT run its refresh→clear-storage→redirect path:
 * doing so reloads the login page, wipes the inline error, and throws away a
 * still-valid code the user merely mistyped (also blocking the OTP attempt-cap).
 * The caller handles these 401s by showing the error inline.
 */
function isAuthFlowRequest(url?: string): boolean {
  if (!url) return false;
  // Anchored to full path segments (…(\/|$)) so a FUTURE authenticated route such
  // as /auth/login-history or /auth/reset-preferences can't accidentally match and
  // skip the refresh-on-401 path. Matches only the actual pre-auth endpoints.
  return /\/(verify-code|request-code)(\/|$)|\/auth\/(login|reset)(\/|$)/.test(url);
}

/**
 * A 401 that means "you typed the wrong password", not "your session died".
 *
 * `/auth/password` is authenticated, so it cannot be exempted by ROUTE — that
 * would also have to hold for a genuinely expired session on the same endpoint,
 * which then has no way to heal. The server distinguishes the two for us.
 */
function isCredentialRejection(error: any): boolean {
  return error?.response?.data?.code === 'CURRENT_PASSWORD_INVALID';
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Shared refresh state — prevents concurrent refresh race condition
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const opsAccess = sessionStorage.getItem('opsOrgAccess');
  if (opsAccess) {
    try {
      const parsed = JSON.parse(opsAccess);
      if (parsed.accessToken) {
        config.headers['X-Ops-Access-Token'] = parsed.accessToken;
      }
    } catch { /* ignore */ }
  }

  return config;
});

/**
 * Make a validation 400 legible ONCE, for every caller.
 *
 * The server's validation envelope carries the field and the reason
 * (`errors: [{field, message}]`), and `extractApiErrorMessage` turns that into
 * "Email is not a valid email address". But ~260 catch blocks across this app
 * read `err.response.data.message` directly — so every one of them showed the
 * headline alone ("Validation failed", "Invalid resident payload") while the
 * useful half sat unread in the payload beside it.
 *
 * Converting 260 call sites is not the fix; the fix is that the message a
 * caller reads is already the readable one. Rewriting it here does that for all
 * of them at once, including every catch block written after this. Only touches
 * a `VALIDATION_ERROR` that actually carries field errors — every other
 * response, code and status is handed back exactly as the server sent it.
 *
 * BOTH message slots, not just `data.message`. 39 catch sites across 12 files
 * (Work Orders, My Queue, Estimates, Make-Ready…) read `data.error.message`
 * FIRST and fall through to `data.message` only when it is absent. Rewriting
 * one slot and not the other left those 39 reading the raw Zod sentence while
 * the readable one sat unused beside it — the exact defect this function was
 * added to remove, reintroduced one property along.
 */
export function makeValidationMessageReadable(error: unknown): void {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!data || data.code !== 'VALIDATION_ERROR') return;
  if (!Array.isArray(data.errors) || data.errors.length === 0) return;
  const readable = extractApiErrorMessage(error);
  if (!readable) return;
  data.message = readable;
  const nested = data.error;
  if (nested && typeof nested === 'object') {
    (nested as Record<string, unknown>).message = readable;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    makeValidationMessageReadable(error);

    // Email-link session hit a step-up-gated route (spec 102 §4.7): route to
    // interactive re-login (which upgrades the session, dropping `amr`),
    // preserving where they were so returnTo brings them back.
    if (error.response?.status === 403 && error.response?.data?.code === 'STEP_UP_REQUIRED') {
      if (typeof window !== 'undefined') {
        const base = loginUrlWithNext('/login');
        window.location.href = base.includes('?') ? `${base}&reauth=1` : `${base}?reauth=1`;
      }
      return Promise.reject(error);
    }

    // A 401 from a login/reset endpoint is bad credentials, not an expired
    // session — hand it straight back so the login page shows the error inline
    // and the user can retry (up to the OTP attempt cap) without a page reload.
    if (
      error.response?.status !== 401 ||
      original._retry ||
      isAuthFlowRequest(original?.url) ||
      isCredentialRejection(error)
    ) {
      return Promise.reject(error);
    }

    original._retry = true;
    const userType = localStorage.getItem('userType');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = loginUrlWithNext(userType === 'ops' ? '/ops/login' : '/login');
      return Promise.reject(error);
    }

    // If a refresh is already in flight, queue this request to retry after it completes
    if (isRefreshing) {
      return new Promise((resolve) => {
        addRefreshSubscriber((newToken: string) => {
          original.headers.Authorization = `Bearer ${newToken}`;
          resolve(api(original));
        });
      });
    }

    isRefreshing = true;

    const refreshEndpoint = userType === 'ops'
      ? `${API_BASE}/ops/auth/refresh`
      : `${API_BASE}/auth/refresh`;

    const loginRedirect = loginUrlWithNext(userType === 'ops' ? '/ops/login' : '/login');

    try {
      const { data } = await axios.post(refreshEndpoint, { refreshToken });
      const newAccessToken = data.data.accessToken;
      localStorage.setItem('accessToken', newAccessToken);
      localStorage.setItem('refreshToken', data.data.refreshToken);
      isRefreshing = false;

      // Replay this request + all queued requests with the new token
      onRefreshed(newAccessToken);
      original.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(original);
    } catch (refreshErr: any) {
      isRefreshing = false;
      refreshSubscribers = [];
      /**
       * Only a REFUSED refresh ends the session. A 502/timeout means the server
       * could not answer — the refresh token is very likely still good, and
       * wiping storage here signs the user out of a working session AND (since
       * `AuthContext`'s cross-tab guard treats `clear()` as a sign-out) reloads
       * every other tab to the login screen with it. Same shape as the
       * `/auth/me` boot catch; both fixed together. Audit 2026-08-17.
       */
      const status = refreshErr?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = loginRedirect;
      }
      return Promise.reject(error);
    }
  },
);

export default api;
